import { toError } from "@clipboard-health/util-ts";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useIsKeyboardOpen } from "@src/appV2/lib/utils/capacitor/useIsKeyboardOpen";
import { AddRolesErrorState } from "@src/appV2/redesign/Roles/AddRoles/ErrorState";
import { AddRolesFooter } from "@src/appV2/redesign/Roles/AddRoles/Footer";
import { AddRolesLoadingState } from "@src/appV2/redesign/Roles/AddRoles/LoadingState";
import { useAddRolesData } from "@src/appV2/redesign/Roles/AddRoles/useAddRolesData";
import { useCreateShoppableQualification } from "@src/appV2/redesign/Roles/api/useCreateShoppableQualification";
import { useDeleteShoppableQualification } from "@src/appV2/redesign/Roles/api/useDeleteShoppableQualification";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { type ReactElement, useState } from "react";
import { useHistory } from "react-router-dom";

import { PageWrapper } from "../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../components/StickyScrollPageHeader";
import { AddRolePageContent } from "../../../Roles/AddRoles/PageContent";

export function AccountAddRolesPage(): ReactElement {
  const {
    allQualifications,
    qualificationsByCategories,
    qualificationsAreLoading,
    qualificationsAreLoaded,
    qualificationsAreError,
    autoApprovedQualifications,
    workerId,
    scrollContainerRef,
    workerQualificationIds,
    qualificationNameToCredentialMap,
    shoppableQualifications,
  } = useAddRolesData();

  const { showErrorToast } = useToast();
  const history = useHistory();

  const [localAddedQualificationIds, setLocalAddedQualificationIds] = useState<string[]>([]);

  const isKeyboardOpen = useIsKeyboardOpen({ keyboardOpenEvent: "keyboardWillShow" });

  const { mutateAsync: createShoppableQualification, isLoading: isCreatingShoppableQualification } =
    useCreateShoppableQualification();

  const { mutateAsync: deleteShoppableQualification, isLoading: isDeletingShoppableQualification } =
    useDeleteShoppableQualification();

  if (qualificationsAreError) {
    return <AddRolesErrorState />;
  }

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader
        title="Add More Roles"
        headerVariant="tertiary"
        scrollContainerRef={scrollContainerRef}
      />

      <PageContent>
        {qualificationsAreLoading && <AddRolesLoadingState />}

        {qualificationsAreLoaded && (
          <AddRolePageContent
            ref={scrollContainerRef}
            qualificationsByCategories={qualificationsByCategories}
            autoApprovedQualifications={autoApprovedQualifications}
            qualificationNameToCredentialMap={qualificationNameToCredentialMap}
            workerQualificationIds={workerQualificationIds}
            onCreateShoppableQualification={async (qualificationId) => {
              try {
                await createShoppableQualification({
                  workerId,
                  qualificationId,
                });

                setLocalAddedQualificationIds((previous) => [...previous, qualificationId]);

                logEvent(APP_V2_USER_EVENTS.ROLE_ADDED, {
                  qualificationId,
                  workerId,
                  screen: "ACCOUNT",
                });
              } catch (maybeError) {
                const error = toError(maybeError);

                logError(APP_V2_APP_EVENTS.ADD_ROLE_FAILURE, {
                  metadata: {
                    qualificationId,
                    workerId,
                    screen: "ACCOUNT",
                  },
                  error,
                });
                throw error;
              }
            }}
          />
        )}
      </PageContent>

      <AddRolesFooter
        addedQualificationIds={localAddedQualificationIds}
        qualifications={allQualifications}
        shoppableQualifications={shoppableQualifications}
        isVisible={isKeyboardOpen}
        canContinue={
          localAddedQualificationIds.length > 0 &&
          !isCreatingShoppableQualification &&
          !isDeletingShoppableQualification
        }
        onDeleteRole={async (shoppableQualificationId, qualificationId) => {
          await deleteShoppableQualification({
            workerId,
            shoppableQualificationId,
          });

          setLocalAddedQualificationIds((previous) =>
            previous.filter((id) => id !== qualificationId)
          );

          logEvent(APP_V2_USER_EVENTS.ROLE_REMOVED, {
            qualificationId,
            workerId,
            isOnboarding: true,
            screen: "ACCOUNT",
          });
        }}
        onDeleteRoleError={async (qualificationId, error) => {
          showErrorToast("Something went wrong while removing the role. Please try again.");

          logError(APP_V2_APP_EVENTS.REMOVE_ROLE_FAILURE, {
            metadata: {
              qualificationId,
              workerId,
              isOnboarding: true,
              screen: "ACCOUNT",
            },
            error,
          });
        }}
        onDone={async () => {
          history.goBack();
        }}
      />
    </PageWrapper>
  );
}
